import { kMatterBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { CreateOrUpdateMatterForm, MatterRead, MatterReadSchema } from '../store/schemas'
import { ApiError } from '@/util/errors/api-error'

/**
 * Put Update Matter
 *
 * Calls the backend endpoint to update an existing matter for the current user.
 * @param matterId - UUID of the matter to update.
 * @param matterFormData - Updated values for name, client, and description.
 * @returns The updated MatterRead object.
 */
export async function putUpdateMatter(matterId: string, matterFormData: CreateOrUpdateMatterForm): Promise<MatterRead> {
  const sentryTransactionId = nanoid()

  try {
    const user = getAuth().currentUser
    if (!user) throw new Error('User not authenticated.')
    const token = await user.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(matterFormData),
      method: 'PUT',
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMatterBasePath}${matterId}`
    const response = await fetch(apiUrl, requestOptions)
    const data = await response.json()

    if (!response.ok) {
      // No need to log 409s to Sentry — these happen when a matter with the same name exists,
      // and are handled by showing an error to the user in the UI
      if (response.status !== 409) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentryTransactionId })
          Sentry.captureException(new Error(`Fetch error - status: ${response.status}`), {
            extra: {
              requestUrl: apiUrl,
              requestOptions,
              response: JSON.stringify(response),
              data: JSON.stringify(data),
              onLine: navigator.onLine,
              cookieEnabled: navigator.cookieEnabled,
              matterId,
              matterFormData: JSON.stringify(matterFormData),
            },
          })
        })
      }

      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`, data.detail)
    }

    return MatterReadSchema.parse(data)
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled, matterId, matterFormData: JSON.stringify(matterFormData) },
        })
      })
    }
    throw error
  }
}
